import { saveAs } from 'file-saver';
import { createSearchParams, generatePath, matchPath, type PathMatch } from 'react-router-dom';
import { type Article } from '../store/article/articleModels';
import { type AccessModel, type AuthModel } from '../store/auth/authModels';
import { type ProductWarrantyType } from '../store/product/productModels';
import { type SSOItem } from '../store/univers/universModels';
import { APP_URL, DEFAULT_COUNTRY, DEFAULT_COUNTRY_LANGUAGE_SEPARATOR, DEFAULT_LANGUAGE, LOCAL_STORAGE_KEYS, SLUG_ACCESS_KEYS, UNIVERS, WarrantyType } from './defines';
import { getLocalStorageItem, setLocalStorageItem, removeLocalStorageItem /*, setLocalStorageItemTtl*/ } from '../utils/helpers/localStorage';
import { env } from "env";
import { proxyConfig } from '../configs/proxyConfig';

export const isLocalhost = Boolean(window.location.hostname.includes('localhost'));

export const getTargetApiProxy = () => {
  return globalThis.localStorage?.getItem('proxy')?.trim().toLowerCase() || '';
};
export const getTargetApiHeaders = (headers: Record<string, string> = {}) => ({
  ...headers,
  ...(typeof globalThis.localStorage === 'undefined'
    ? {}
    : (() => {
        const targetApi = getTargetApiProxy();
        const targetHeaders: Record<string, string> = {};
        if (targetApi === 'dotnet' || targetApi === 'php') {
          targetHeaders['X-Target-Api'] = targetApi;
        }
        return targetHeaders;
      })())
});

export const fetchWithTargetApi = (input: RequestInfo | URL, init: RequestInit = {}) => {
  const headers = getTargetApiHeaders(
    Object.fromEntries(new Headers(init.headers).entries()) as Record<string, string>
  );

  return fetch(input, {
    ...init,
    headers,
  });
};

const REDIRECT_FALLBACK_PATH = '/';
const DEFAULT_ALLOWED_EXTERNAL_REDIRECT_HOSTS = [
  'apis-tst.groupe-atlantic.com',
  'apis.groupe-atlantic.com',
  'apis-pre.groupe-atlantic.com',
];

const normalizeHostName = (value: string) => {
  const trimmed = value.trim();
  const cleaned = trimmed.length > 1 && ((trimmed.startsWith('"') && trimmed.endsWith('"')) || (trimmed.startsWith("'") && trimmed.endsWith("'")))
    ? trimmed.slice(1, -1)
    : trimmed;
  if (!cleaned) {
    return '';
  }

  try {
    return new URL(cleaned).hostname.toLowerCase();
  } catch {
    try {
      return new URL(`https://${cleaned}`).hostname.toLowerCase();
    } catch {
      return '';
    }
  }
};

const getAllowedExternalRedirectHosts = () => {
  const fromEnv = (env.allowed_redirect_hosts || '')
    .split(',')
    .map((host) => normalizeHostName(host))
    .filter(Boolean);

  const defaults = DEFAULT_ALLOWED_EXTERNAL_REDIRECT_HOSTS
    .map((host) => normalizeHostName(host))
    .filter(Boolean);

  return new Set([...defaults, ...fromEnv]);
};

const safeDecodeURIComponent = (value: string) => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const removeInvisibleChars = (value: string) =>
  value
    .split('')
    .filter((char) => {
      const code = char.charCodeAt(0);
      return code > 32 && code !== 127;
    })
    .join('');

const hasBlockedRedirectPattern = (value: string) => {
  const compact = removeInvisibleChars(value.toLowerCase());
  const decoded = safeDecodeURIComponent(compact);

  const blocked = [compact, decoded];
  return blocked.some(
    (candidate) =>
      candidate.startsWith('javascript:') ||
      candidate.startsWith('data:') ||
      candidate.startsWith('vbscript:') ||
      candidate.startsWith('file:') ||
      candidate.startsWith('//') ||
      candidate.includes('\\')
  );
};

const parseRedirectUrl = (value: string) => {
  try {
    return new URL(value, globalThis.location.origin);
  } catch {
    return null;
  }
};

const toSafeRelativePath = (parsedUrl: URL) => {
  const path = `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
  return path || REDIRECT_FALLBACK_PATH;
};

const sanitizeUrlInput = (url: string) => {
  if (!url) {
    return '';
  }
  const trimmed = url.trim();
  if (trimmed.length > 1 && ((trimmed.startsWith('"') && trimmed.endsWith('"')) || (trimmed.startsWith("'") && trimmed.endsWith("'")))) {
    return trimmed.slice(1, -1);
  }
  return trimmed;
};

const sanitizeInternalRedirectUrl = (url: string) => {
  const candidate = sanitizeUrlInput(url);
  if (!candidate || hasBlockedRedirectPattern(candidate)) {
    return REDIRECT_FALLBACK_PATH;
  }

  const parsedUrl = parseRedirectUrl(candidate);
  if (parsedUrl?.origin !== globalThis.location.origin) {
    return REDIRECT_FALLBACK_PATH;
  }

  return toSafeRelativePath(parsedUrl);
};

export const correctExternalUrl = (url: string) => {
  const candidate = sanitizeUrlInput(url);
  if (!candidate || hasBlockedRedirectPattern(candidate)) {
    return REDIRECT_FALLBACK_PATH;
  }

  const parsedUrl = parseRedirectUrl(candidate);
  if (!parsedUrl || !['http:', 'https:'].includes(parsedUrl.protocol)) {
    return REDIRECT_FALLBACK_PATH;
  }

  if (parsedUrl.origin === globalThis.location.origin) {
    return toSafeRelativePath(parsedUrl);
  }

  const allowedHosts = getAllowedExternalRedirectHosts();
  if (!allowedHosts.has(parsedUrl.hostname.toLowerCase())) {
    return REDIRECT_FALLBACK_PATH;
  }

  return parsedUrl.toString();
};

export const getAppUrl = (
  url: string,
  options?: {
    routeParams?: Record<string, string>;
    searchParams?: Record<string, string>;
  }
) => {
  let languagesData,
    languagesJson = getLocalStorageItem(LOCAL_STORAGE_KEYS.Language);

  if (languagesJson) {
    languagesData = JSON.parse(languagesJson);
  }

  let countryData,
    countryJson = getLocalStorageItem(LOCAL_STORAGE_KEYS.Country);

  if (countryJson && countryJson !== 'undefined') {
    countryData = JSON.parse(countryJson);
  }

  const { routeParams, searchParams } = options ?? {};

  let result = !routeParams
    ? url
    : generatePath(
      url,
      routeParams
        ? Object.fromEntries(
          Object.entries(routeParams).map((o) => [o[0], encodeURIComponent(o[1])])
        )
        : {}
    );

  if (searchParams) {
    const sp = createSearchParams(searchParams).toString();
    result = `${result}?${sp}`;
  }
  const pattern = matchPath({ path: APP_URL.Base, end: false }, window.location.pathname) as PathMatch;

  if (languagesData) {
    if (pattern) {
      const langFromPattern = languagesData?.languages?.some((l: any) => {
        return l.lang === pattern.params.lang;
      });

      if (langFromPattern) {
        return `${pattern?.pathname || ''}/${result}`;
      } else {
        return `/${countryData?.code || DEFAULT_COUNTRY}${DEFAULT_COUNTRY_LANGUAGE_SEPARATOR}${languagesData.currentLanguage || DEFAULT_LANGUAGE}/${result}`;
      }
    } else {
      return `/${countryData?.code || DEFAULT_COUNTRY}${DEFAULT_COUNTRY_LANGUAGE_SEPARATOR}${languagesData.currentLanguage || DEFAULT_LANGUAGE}/${result}`;
    }
  } else {
    return `/${DEFAULT_COUNTRY}${DEFAULT_COUNTRY_LANGUAGE_SEPARATOR}${DEFAULT_LANGUAGE}/${result}`;
  }
};

export const getUniversFromUrl = (domain?: string) => {
  let host = window.location?.host;
  //domain = UNIVERS.Other //UNIVERS.ThermorFR
  domain = UNIVERS.AtlanticFR;

  Object.values(UNIVERS).some((univers) => {
    if (getUrlByUniverse(univers)?.includes(host)) {
      domain = univers;
      return true;
    }
    return null;
  });
  return domain;
};

// @ts-ignore
export const getApiUrlBase = (univers: string) => {
  let pattern = env.api_url;
  let end_point = env.api_end_point;
  let proxy_url = proxyConfig.baseUrl;
  return proxy_url ?? pattern! + end_point!;
};

export const getUrlByUniverse = (univers: string) => {
  let url: string | undefined = '';
  if (isLocalhost) {
    const parts = window.location?.host?.split('.');
    const index = parts.findIndex((p) => p.startsWith('localhost'));
    url = 'http://' + [univers, ...parts.slice(index)].join('.'); // NOSONAR
  } else {
    switch (univers) {
      case UNIVERS.Atlantic:
        url = env.app_url_atlantic_int;
        break;
      case UNIVERS.AtlanticFR:
        url = env.app_url_atlantic_fr;
        break;
      case UNIVERS.Thermor:
        url = env.app_url_thermor_int;
        break;
      case UNIVERS.ThermorFR:
        url = env.app_url_thermor_fr;
        break;
      case UNIVERS.Acv:
        url = env.app_url_acv;
        break;
      case UNIVERS.AcvFR:
        url = env.app_url_acv_fr;
        break;
      case UNIVERS.AutresMarques:
        url = env.app_url_autres_marques;
        break;
      case UNIVERS.EquationWelcome:
        url = env.app_url_equation_welcome;
        break;
      case UNIVERS.Oem:
        url = env.app_url_oem;
        break;
      case UNIVERS.Pacific:
        url = env.app_url_pacific;
        break;
      case UNIVERS.Sauter:
        url = env.app_url_sauter;
        break;
      case UNIVERS.Ygnis:
        url = env.app_url_ygnit_int;
        break;
      case UNIVERS.Orcon:
        url = env.app_url_orcon_int;
        break;
    }
  }
  return url;
};

export const getApiUrl = (univers: string) => {
  return `${getApiUrlBase(univers)}/api`;
};

export const getUniversForUrl = (brand: string, forUniversRequest?: boolean) => {
  switch (brand) {
    case UNIVERS.AtlanticFR:
      return UNIVERS.Atlantic;
    case UNIVERS.AcvFR:
      return UNIVERS.Acv;
    case UNIVERS.ThermorFR:
      return forUniversRequest ? UNIVERS.EspaceSav : UNIVERS.Thermor;
  }
  return brand;
};

export const getBrandFolder = (brand: string) => {
  switch (brand) {
    case UNIVERS.AtlanticFR:
    case UNIVERS.ThermorFR:
      return getUniversForUrl(brand);
  }
  return 'other';
};

export const getSSOByUnivers = () => {
  let univers = getUniversFromUrl();
  switch (univers) {
    case UNIVERS.Atlantic:
    case UNIVERS.Acv:
    case UNIVERS.Thermor:
    case UNIVERS.Ygnis:
    case UNIVERS.Orcon:
    case UNIVERS.AutresMarques:
    case UNIVERS.EquationWelcome:
    case UNIVERS.Oem:
      return "inter";
    case UNIVERS.AtlanticFR:
      return "atlantic";
    case UNIVERS.ThermorFR:
      return "thermor";
    case UNIVERS.AcvFR:
      return "acv";
    case UNIVERS.Sauter:
      return "sauter";
    case UNIVERS.Pacific:
      return "pacific";
  }

  return univers;
}

export const isGroupSSO = (listSSO: any) => {
  const lst_group = listSSO?.filter((u: { sso_code: string | string[] }) => {
    return u.sso_code && (u.sso_code.indexOf('group') !== -1 || u.sso_code.indexOf('inter') !== -1);
  });

  return lst_group && lst_group.length > 0;
};

export const getProSSO = (listSSO: any) => {
  return listSSO
    ?.filter((sso: any) => [UNIVERS.Atlantic, UNIVERS.Thermor].includes(sso.sso_code))
    .map((sso: any) => {
      return {
        ...sso,
        url: getSSOUrl(sso.sso_code),
      };
    });
};

export const getSSOListWithUrl = (listSSO: any) => {
  return listSSO?.map((sso: any) => {
    return {
      ...sso,
      url: getSSOUrl(sso.sso_code),
    };
  });
};

export function getSSOUrl(sso_code: string) {
  let univers = getUniversFromUrl();
  return getApiUrlBase(univers) + '/api/public/redirect/' + univers.replace('-fr', '') + '/' + sso_code;
}

export const getActiveCountries = (countrys: any) => {
  //return countrys?.filter((country: any) => country.actifs === 1);
  let actifs:any = [];
  countrys?.map((country: any) => {
      if (country.actifs === 1) {
        actifs.push(country);
      }
  });
  return actifs;
  //return countrys;
}

export const checkAccess = (user: AuthModel, accesSlug: string) => {
  let result = false;

  if (user?.acces) {
    result = user.acces.filter((acces: AccessModel) => acces.slug === accesSlug).length > 0;
  }
  return result;
};

export const isArticleAvailable = (article: Article) => {
  return article && article.articleReseau?.statut !== 4 && article.articleReseau?.statut !== 5;
};
export const getArticlePriceHT = (article: Article, listSSO: any, isLoggedIn: any) => {
  return isArticleAvailable(article) && (isLoggedIn || !isGroupSSO(listSSO)) && article?.articleReseau.prix_ht;
};
// @ts-ignore
export const allowArticleAddToCard = (article: Article, listSSO: any, user: any) => {
  return (
    !article.ajoutPanier ||
    (article.articleReseau.prix_ht || 0) <= 0 ||
    article.article_uuid ||
    !article.articleReseau.isCommandable ||
    !isArticleAvailable(article)
  );
};

export const showArticleCreditCardIcon = (article: Article, user: AuthModel) => {
  let { articleReseau, paiementCb, isEnCompte } = article;
  const isLoggedIn = user?.active;
  const hasAccessCommande = isLoggedIn && checkAccess(user, SLUG_ACCESS_KEYS.Commande);
  return (
    isLoggedIn &&
    !isEnCompte &&
    hasAccessCommande &&
    paiementCb &&
    articleReseau?.disponibilite &&
    articleReseau?.isCommandable &&
    (articleReseau?.prix_ht as number) > 0
  );
};

export const disableArticleAddtoCartButton = (
  article: Article,
  user: AuthModel,
  listeSSO: SSOItem[],
  disabledLogin: boolean
) => {
  let { articleReseau, article_uuid, ajoutPanier } = article;
  const isLoggedIn = user?.active;
  const proSSOList = getProSSO(listeSSO);
  const canViewDiscountedPrice = checkAccess(user, SLUG_ACCESS_KEYS.SeeDiscountedPrice);

  return (
    (!isLoggedIn && (disabledLogin || !proSSOList?.length)) ||
    (!articleReseau) ||
    (articleReseau?.prix_ht as number) <= 0 ||
    !!article_uuid ||
    !articleReseau?.isCommandable ||
    !isArticleAvailable(article) ||
    (isLoggedIn && (!canViewDiscountedPrice || !ajoutPanier))
  );
};

export const getBreadcrumbQueryParams = (locationPath: string, id?: string, reseau_uuid?: string) => {
  if (matchPath({ path: APP_URL.Base + '/' + APP_URL.Family, end: true }, locationPath)) {
    return { famille_uuid: id };
  } else if (matchPath({ path: APP_URL.Base + '/' + APP_URL.ProductReseau, end: true }, locationPath)) {
    return { produit_uuid: id, reseau_uuid };
  } else if (
    matchPath({ path: APP_URL.Base + '/' + APP_URL.Product, end: true }, locationPath) ||
    matchPath({ path: APP_URL.Base + '/' + APP_URL.ProductSchema, end: true }, locationPath) ||
    matchPath({ path: APP_URL.Base + '/' + APP_URL.ProductReseau, end: true }, locationPath)
  ) {
    return { produit_uuid: id };
  } else if (matchPath({ path: APP_URL.Base + '/' + APP_URL.Article, end: true }, locationPath)) {
    return { article_uuid: id };
  } else if (matchPath({ path: APP_URL.Base + '/' + APP_URL.ArticleReseau, end: true }, locationPath)) {
    return { article_uuid: id, reseau_uuid };
  }
  return null;
};

export const renderPrice = (price: number, currency?: string) => {
  if (price > 0) return formatNumberEuro(price, currency);
  else return 'article.prix.consult';
};
export const formatNumberEuro = (price: number, currency?: string) => {
  price = price ?? 0;
  const text = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(price);
  if (currency) {
    return text.replace("€", currency);
  }

  return text;
};

export const getDocGARight = (tags: string) => {
  const droits = ['PFS_PUBLIC', 'PFS_PRO', 'PFS_SATC', 'PFS_SAVA', 'PFS_CMISTE'];

  let documentTags = tags.split(','),
    finalTags: any[] = [];

  documentTags.forEach((tag) => {
    if (droits.includes(tag)) finalTags.push(tag);
  });

  return finalTags;
};

/**
 * Returns client type by user and reseau uuid
 * @param  {[AuthModel]} user
 * @param  {[string]} reseau_uuid
 * @return {[Boolen]}
 */
export function getClientType(user: AuthModel, reseau_uuid: string) {
  if (!user.societe || !user.societe.societe_reseau) return null;
  let societe_reseau = user.societe.societe_reseau.find((sr) => sr.reseau_uuid === reseau_uuid);
  if (!societe_reseau) return null;
  return societe_reseau.station_sav ? 'station-sav' : 'client-en-compte';
}

/**
 * Retourne si le type de garantie est compatatible avec le type de demande
 * @param  { [ProductWarrantyType]} typeGarantie
 * @param  { [string]} warrantyRequstType
 * @return { [Boolean]}
 */

export function isTypeGarantiesAllowed(typeGarantie: ProductWarrantyType, warrantyRequstType: string) {
  let typeAllowed: string[] = [];
  if (warrantyRequstType === WarrantyType.Commande) {
    typeAllowed = ['fiche-dincident', 'fiche-dincident-mod', 'avg', 'avg-mod'];
  } else if (warrantyRequstType === WarrantyType.Retour) {
    typeAllowed = ['fiche-dincident', 'fiche-dincident-mod'];
  }
  return typeGarantie && typeAllowed.indexOf(typeGarantie.slug) > -1;
}

export function isWarrantyRequestAllowedForUniverse(univers: string) {
  return univers !== UNIVERS.Sauter;
}


export const loadResourceFromServer = (url: string, callback: any) => {
  const fetchOptions = {
    method: "GET",
  };
  let params = {
    ...fetchOptions,
  };
  fetchWithTargetApi(url, params)
    .then((response) => response.arrayBuffer())
    .then((buffer: ArrayBuffer) => {
      const blob = new Blob([new Uint8Array(buffer, 0, buffer.byteLength)]);
      const reader = new FileReader();
      reader.onload = (event: any) => {
        const dataUrl = event.target.result;
        callback(dataUrl);
        // do something with base64
      };

      reader.readAsDataURL(blob);

    }).catch();
}

export const navigateToUrl = (url: string) => {
  window.location.href = correctExternalUrl(url); // NOSONAR
}


export const redirectToUrlForUser = (url: string) => {
  let headers = {
    "Content-Type": "application/x-www-form-urlencoded"
  };
  if (isLocalhost) {
    headers = {
      "Content-Type": "application/x-www-form-urlencoded"
    } as any;
  }
  const fetchOptions = {
    method: "POST",
  };
  let params = {
    ...fetchOptions,
    headers,
  };
  fetchWithTargetApi(url, params)
    .then((response) => response.json())
    .catch((response) => {
      if (response.error?.originalStatus === 401 || response.error?.status === 401) {
        //window.location.reload();
        globalThis.location.href = "/logout";
      }
    })
    .then((response) => {
      //debugger
      if (response.redirect_url) {
        navigateToUrl(response.redirect_url);
      }
    });
}


export const loginConnectAs = (url: string, callback: any, onError: any) => {
  const fetchOptions = {
    method: "GET",
  };
  let params = {
    ...fetchOptions,
  };

  try {
    fetchWithTargetApi(url, params)
      .then((response) => response.json())
      .catch((response) => {
        onError && onError(response);
      })
      .then((response) => {
        //debugger
        if (response && response.token) {
          setLocalStorageItem(LOCAL_STORAGE_KEYS.SessionCode, response.session_code);

          //setLocalStorageItem(LOCAL_STORAGE_KEYS.UserToken, response.token)
          //setLocalStorageItem(LOCAL_STORAGE_KEYS.RefreshToken, "")
          //setLocalStorageItem(LOCAL_STORAGE_KEYS.SessionCode, response.session_code)
          callback && callback(response);
        }
      });
  }
  catch (error) {
    onError && onError();
  }
}



export const getLogoutCallbackUrl = (url: string, callback: any, onError: any) => {

  const session_code = getLocalStorageItem(LOCAL_STORAGE_KEYS.SessionCode);
  if (session_code) {
    url += `&session_code=${session_code}`;
  }

  const fetchOptions = {
    method: "GET",
  };
  let params = {
    ...fetchOptions,
  };

  try {
    fetchWithTargetApi(url, params)
      .then((response) => response.json())
      .catch((response) => {
        onError && onError(response);
      })
      .then((response) => {
        //debugger
        //console.log(response);
        if (response && response.back_url) {
          removeLocalStorageItem(LOCAL_STORAGE_KEYS.SessionCode);
          removeLocalStorageItem(LOCAL_STORAGE_KEYS.RefreshToken);
          removeLocalStorageItem(LOCAL_STORAGE_KEYS.AppToken);
          removeLocalStorageItem(LOCAL_STORAGE_KEYS.LoginState);
          callback && callback(correctExternalUrl(response.back_url));
        }
      });
  }
  catch (error) {
    onError && onError();
  }

}


export const downloadFromUrl = (url: string, callback: any) => {

  let session_code = getLocalStorageItem(LOCAL_STORAGE_KEYS.SessionCode);
  if (session_code && url.indexOf("session_code") === -1) {
    if (url.indexOf("?") === -1) {
      url += `?session_code=${session_code}`;
    } else {
      url += `&session_code=${session_code}`;
    }
  }
  const fetchOptions = {
    method: "GET",
    credentials: 'include'
  };
  const params = {
    ...fetchOptions,
  };
  fetchWithTargetApi(url, params as any)
    .then((response) => response.blob())
    .then((blob: Blob) => {
      const imageUrl = URL.createObjectURL(blob);
      callback(imageUrl, blob);
    }).catch();

}

const getFileNameFromContentDisposition = (contentDisposition: string | null): string | undefined => {
  if (!contentDisposition) {
    return undefined;
  }

  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
  if (utf8Match?.[1]) {
    return safeDecodeURIComponent(utf8Match[1]);
  }

  const asciiMatch = /filename="?([^";]+)"?/i.exec(contentDisposition);
  return asciiMatch?.[1];
};

export const downloadFileFromUrl = async (url: string, fallbackFileName: string = 'export') => {
  let session_code = getLocalStorageItem(LOCAL_STORAGE_KEYS.SessionCode);
  let requestUrl = url;
  if (session_code && requestUrl.indexOf('session_code') === -1) {
    requestUrl += (requestUrl.indexOf('?') === -1 ? '?' : '&') + `session_code=${session_code}`;
  }

  const response = await fetchWithTargetApi(requestUrl, { method: 'GET', credentials: 'include' });
  if (!response.ok) {
    throw new Error(`Download failed with status ${response.status}`);
  }

  const blob = await response.blob();
  const fileName = getFileNameFromContentDisposition(response.headers.get('content-disposition')) ?? fallbackFileName;
  saveAs(blob, fileName);
};


const app_back_api_url = env.api_token_url;


export const getMaintenance = (maintenanceCallback: any) => {
  const f = () => {
    let headers = { "Content-Type": "application/x-www-form-urlencoded" };
    const fetchOptions = {
      method: "GET"
    };
    let params = {
      ...fetchOptions,
      headers,
    };

    fetchWithTargetApi(`${app_back_api_url ?? ''}/api/getMaintenance`, params)
      .then((response) => {
        return response.json()
      })
      .then((response) => {
        if (response) { // NOSONAR
          maintenanceCallback && maintenanceCallback(response);
        }
      })
      .catch((error) => {
        console.log(error);
      });

  }
  setInterval(f, 5 * 60000);
  f();

}

export const getAppInsightsData = (successCallback: any) => {
  const f = () => {
    let headers = { "Content-Type": "application/x-www-form-urlencoded" };
    const fetchOptions = {
      method: "GET"
    };
    let params = {
      ...fetchOptions,
      headers,
    };

    fetchWithTargetApi(`${app_back_api_url ?? ''}/api/getAppInsights`, params)
      .then((response) => {
        return response.json()
      })
      .then((response) => {
        if (response) { // NOSONAR
          successCallback?.(response);
        }
      })
      .catch((error) => {
        console.log(error);
      });

  }
  f();

}

// Récupérer le token CSRF depuis les cookies
export const getCsrfToken = () => {
  const cookies = document.cookie.split(';');
  //console.log('🍪 Tous les cookies:', document.cookie)

  const csrfCookie = cookies.find(cookie =>
    cookie.trim().startsWith('csrf=')
  );
  //console.log('🔍 Cookie CSRF trouvé:', csrfCookie)

  return csrfCookie ? csrfCookie.split('=')[1].trim() : null;
};


export const correctUrl = (url: string) => {
  return sanitizeInternalRedirectUrl(url);
}